import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from 'react-toastify';
import Select from 'react-select';
import type { ClassNamesConfig } from 'react-select';

import { FaRegClock, FaCrosshairs } from 'react-icons/fa';

import {
  getTimezones,
  updateProjectTimezone,
  updateProjectTimezoneFromGeolocation
} from "../../api/projects";

// The API treats an unset timezone as UTC.
const DEFAULT_TIMEZONE = 'UTC';

type TimezoneOption = { value: string; label: string };

interface ProjectTimezoneSettingsProps {
  projectId: string;
  projectData: ProjectInterface;
  canEdit: boolean;
  onProjectUpdate?: () => void;
}

interface DetectionSource {
  latitude: number;
  longitude: number;
  label: string;
}

/** Raised when the browser cannot provide a position (denied, unavailable or timed out). */
class GeolocationUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeolocationUnavailableError';
  }
}

/**
 * Coordinates of the project's default site, when it has one.
 * Preferred over browser geolocation because it describes where the project
 * actually is, not where the person editing it happens to be sitting.
 */
const getDefaultSiteCoordinates = (projectData?: ProjectInterface): DetectionSource | null => {
  const sites = Array.isArray(projectData?.sites) ? projectData.sites : [];
  if (sites.length === 0) return null;

  const readCoordinates = (site: ProjectSiteItemInterface): { latitude: number; longitude: number } | null => {
    const { latitude, longitude } = site?.google_address_data || {};
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return { latitude, longitude };
    }

    // geo_location is stored as [lat, lng] throughout the app
    const geo = site?.geo_location;
    if (Array.isArray(geo) && typeof geo[0] === 'number' && typeof geo[1] === 'number') {
      return { latitude: geo[0], longitude: geo[1] };
    }

    return null;
  };

  // Fall back to the first site with coordinates - projects with a single site
  // often never had one explicitly flagged as default.
  const site = sites.find(s => s.is_default && readCoordinates(s)) || sites.find(s => readCoordinates(s));
  if (!site) return null;

  const coordinates = readCoordinates(site);
  if (!coordinates) return null;

  return { ...coordinates, label: site.name || 'the project location' };
};

/** The browser's own IANA timezone. Needs no permission prompt and no network call. */
const getBrowserTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const isValidTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Building an Intl.DateTimeFormat is expensive and the option list needs one per
 * timezone, so formatters are built once per zone and reused. Only the formatter
 * is cached, never the result - the date is supplied at format time, so DST
 * transitions still produce the right value.
 */
const localTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

const getCachedFormatter = (
  cache: Map<string, Intl.DateTimeFormat>,
  timezone: string,
  build: () => Intl.DateTimeFormat
): Intl.DateTimeFormat | null => {
  const cached = cache.get(timezone);
  if (cached) return cached;

  try {
    const formatter = build();
    cache.set(timezone, formatter);
    return formatter;
  } catch {
    // Unusable timezone - not cached, so a later valid value still gets a chance
    return null;
  }
};

/** Current wall-clock time in the given zone, e.g. "Tue, 14:32". Empty if the zone is unusable. */
const formatLocalTime = (timezone: string, now: Date): string => {
  const formatter = getCachedFormatter(localTimeFormatters, timezone, () =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  );

  return formatter ? formatter.format(now) : '';
};

/** UTC offset label for a zone, e.g. "GMT+2". Empty when the browser cannot produce one. */
const formatOffset = (timezone: string, now: Date): string => {
  const formatter = getCachedFormatter(offsetFormatters, timezone, () =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset'
    })
  );

  if (!formatter) return '';

  return formatter.formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';
};

/**
 * react-select runs `unstyled`, so every visual is Tailwind here rather than an
 * emotion style object. Module scope: it closes over nothing and would otherwise
 * be rebuilt on every render, including each clock tick.
 */
const selectClassNames: ClassNamesConfig<TimezoneOption, false> = {
  container: () => 'text-sm',
  control: ({ isFocused, isDisabled }) =>
    [
      'flex items-center min-h-[40px] w-full px-3 gap-2 rounded-lg border transition-colors',
      isDisabled ? 'bg-[#fafafa] cursor-not-allowed opacity-60' : 'bg-white',
      isFocused ? 'border-[#888]' : 'border-[#eaeaea] hover:border-[#ddd]'
    ].join(' '),
  valueContainer: () => 'flex items-center flex-1 min-w-0 gap-1 py-1.5',
  input: () => 'text-[#171717]',
  placeholder: () => 'text-[#aaa] truncate',
  singleValue: () => 'text-[#171717] truncate',
  indicatorsContainer: () => 'flex items-center text-[#aaa]',
  indicatorSeparator: () => 'hidden',
  dropdownIndicator: () => 'p-1 hover:text-[#444] transition-colors',
  loadingIndicator: () => 'px-1 text-[#888]',
  // `unstyled` still emits react-select's own `z-index: 1` through emotion, which
  // is injected after the Tailwind sheet and would otherwise win on order alone -
  // hence the important modifier, so the menu clears the sticky settings header.
  menu: () =>
    'mt-1 overflow-hidden rounded-lg border border-[#eaeaea] bg-white shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] z-[9999]!',
  menuList: () => 'max-h-60 overflow-y-auto py-1',
  option: ({ isSelected, isFocused }) =>
    [
      'px-3 py-2 cursor-pointer transition-colors',
      isSelected ? 'bg-[#171717] text-white' : isFocused ? 'bg-[#fafafa] text-[#444]' : 'text-[#444]'
    ].join(' '),
  noOptionsMessage: () => 'px-3 py-2 text-[#888]',
  loadingMessage: () => 'px-3 py-2 text-[#888]'
};

const requestBrowserPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new GeolocationUnavailableError('Geolocation is not available in this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      error => reject(new GeolocationUnavailableError(error.message || 'Could not read your location')),
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  });

export const ProjectTimezoneSettings = ({
  projectId,
  projectData,
  canEdit,
  onProjectUpdate
}: ProjectTimezoneSettingsProps) => {
  const savedTimezone: string | null = projectData?.timezone || null;
  const effectiveTimezone = savedTimezone || DEFAULT_TIMEZONE;

  const [selectedTimezone, setSelectedTimezone] = useState<string>(effectiveTimezone);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const timezonesRequested = useRef(false);

  // Keep the local-time preview honest without re-rendering constantly.
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Reset the draft selection whenever the project reloads with a new timezone.
  useEffect(() => {
    setSelectedTimezone(effectiveTimezone);
  }, [effectiveTimezone]);

  const detectionSource = useMemo(() => getDefaultSiteCoordinates(projectData), [projectData]);

  const loadTimezones = useCallback(async () => {
    // A ref rather than the loading state: focus and menu-open can fire in the
    // same tick, and both would still see `isLoadingTimezones` as false.
    if (timezonesRequested.current) return;
    timezonesRequested.current = true;

    setIsLoadingTimezones(true);
    try {
      const list = await getTimezones();
      setTimezones(list);
    } catch (error) {
      console.error('Error loading timezones:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load the list of timezones');
      // Allow another attempt the next time the selector is opened.
      timezonesRequested.current = false;
    } finally {
      setIsLoadingTimezones(false);
    }
  }, []);

  const options: TimezoneOption[] = useMemo(() => {
    // Always keep the current value selectable, even before the list has loaded
    // or if the API list happens not to include it.
    const values = timezones.length > 0 ? timezones : [effectiveTimezone];
    const withCurrent = values.includes(selectedTimezone) ? values : [selectedTimezone, ...values];

    // Offsets are labelled once per list change rather than on every clock tick -
    // they only shift on a DST boundary.
    const referenceDate = new Date();

    return withCurrent.map(timezone => {
      const offset = formatOffset(timezone, referenceDate);
      return { value: timezone, label: offset ? `${timezone} (${offset})` : timezone };
    });
  }, [timezones, effectiveTimezone, selectedTimezone]);

  const selectedOption = options.find(option => option.value === selectedTimezone) || null;
  const hasUnsavedChange = selectedTimezone !== effectiveTimezone;
  const localTime = formatLocalTime(effectiveTimezone, now);

  const applyResolvedTimezone = (timezone: string) => {
    setSelectedTimezone(timezone);
    onProjectUpdate?.();
  };

  const handleSave = async () => {
    if (!projectId || !hasUnsavedChange) return;

    setIsSaving(true);
    try {
      const result = await updateProjectTimezone(projectId, selectedTimezone);
      applyResolvedTimezone(result?.timezone || selectedTimezone);
      toast.success(`Project timezone set to ${result?.timezone || selectedTimezone}`);
    } catch (error) {
      console.error('Error updating project timezone:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update the project timezone');
      setSelectedTimezone(effectiveTimezone);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Resolves and saves in one call - the geolocation endpoint has no dry-run mode.
   * Prefers the default site's coordinates and only asks the browser when the
   * project has no located site.
   */
  const handleDetect = async () => {
    if (!projectId) return;

    setIsDetecting(true);
    try {
      let source = detectionSource;

      if (!source) {
        const position = await requestBrowserPosition();
        source = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          label: 'your current location'
        };
      }

      const result = await updateProjectTimezoneFromGeolocation(
        projectId,
        source.latitude,
        source.longitude
      );

      const resolved = result?.timezone;
      if (!resolved) {
        throw new Error('The API did not return a timezone');
      }

      applyResolvedTimezone(resolved);
      toast.success(`Project timezone set to ${resolved} from ${source.label}`);
    } catch (error) {
      console.error('Error detecting project timezone:', error);

      // A denied or unavailable geolocation prompt is not a dead end: the browser
      // already knows its own zone, so offer it as an unsaved suggestion.
      if (error instanceof GeolocationUnavailableError) {
        const browserTimezone = getBrowserTimezone();

        if (browserTimezone && isValidTimezone(browserTimezone)) {
          setSelectedTimezone(browserTimezone);
          toast.info(`Location unavailable. Selected ${browserTimezone} from your browser - review it and press Save.`);
          return;
        }

        toast.error('Location unavailable. Please pick a timezone from the list.');
        return;
      }

      toast.error(error instanceof Error ? error.message : 'Failed to detect the project timezone');
    } finally {
      setIsDetecting(false);
    }
  };

  const detectLabel = detectionSource ? 'Detect from project location' : 'Detect from my location';
  const detectHint = detectionSource
    ? `Uses the coordinates of ${detectionSource.label} and saves the result immediately.`
    : 'Asks your browser for your location and saves the result immediately.';

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-3">
        <FaRegClock className="text-[#444] w-5 h-5" />
        <h3 className="text-lg font-semibold text-[#171717]">Time Zone</h3>
      </div>

      <div className="bg-[#fafafa] border border-[#eaeaea] rounded-lg p-4 space-y-4">
        <div>
          <div className="flex items-center flex-wrap gap-x-2 text-sm">
            <span className="text-[#888]">Current:</span>
            <span className="font-medium text-[#171717]">{effectiveTimezone}</span>
            {!savedTimezone && (
              <span className="text-[#888]">(not configured - defaulting to UTC)</span>
            )}
            {localTime && <span className="text-[#888]">&middot; local time {localTime}</span>}
          </div>
          <p className="text-sm text-[#888] mt-1">
            Daily update reminders and business-day checks follow this time zone. Daylight saving
            changes are applied automatically.
          </p>
        </div>

        {canEdit && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <Select<TimezoneOption, false>
                  inputId="project-timezone-select"
                  aria-label="Project time zone"
                  options={options}
                  value={selectedOption}
                  onChange={option => option && setSelectedTimezone(option.value)}
                  onMenuOpen={loadTimezones}
                  onFocus={loadTimezones}
                  isLoading={isLoadingTimezones}
                  isDisabled={isSaving || isDetecting}
                  placeholder="Search for a time zone..."
                  noOptionsMessage={() => (isLoadingTimezones ? 'Loading time zones...' : 'No matching time zone')}
                  unstyled
                  classNames={selectClassNames}
                />
              </div>

              <div className="flex items-center gap-2">
                {/* Fixed widths so the pending labels do not resize the buttons */}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasUnsavedChange || isSaving || isDetecting}
                  className="w-[100px] shrink-0 px-4 py-2 bg-[#171717] text-white text-sm font-medium rounded-lg hover:bg-[#333] transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleDetect}
                  disabled={isSaving || isDetecting}
                  title={detectHint}
                  className={`${detectionSource ? 'w-[248px]' : 'w-[216px]'} shrink-0 px-4 py-2 bg-white text-[#444] text-sm font-medium border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <FaCrosshairs className="w-3 h-3" />
                  <span>{isDetecting ? 'Detecting...' : detectLabel}</span>
                </button>
              </div>
            </div>
          </>
        )}

        {!canEdit && (
          <p className="text-xs text-[#aaa]">
            Only project admins and project managers can change the time zone.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProjectTimezoneSettings;
